import { useEffect, useState, useCallback } from 'react'
import {
  Network, List as ListIcon, Map, Users, UserCheck, Layers, Award, TrendingUp, Search,
  Zap, ChevronRight, Clock, Download,
} from 'lucide-react'
import api from '../api/client'
import SearchableSelect from '../components/SearchableSelect'
import Loader from '../components/Loader'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }
const num = (v) => Number(v || 0).toLocaleString('pt-BR')
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dt = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—')
const STATUS_COR = (ativo) => (ativo ? '#16a34a' : '#a8a8a8')

const TABS = [
  ['resumo', 'Resumo', TrendingUp], ['arvore', 'Árvore', Network], ['lista', 'Lista', ListIcon],
  ['clientes', 'Clientes da Rede', Users], ['diretos', 'Patrocinados Diretos', UserCheck],
  ['niveis', 'Equipe por Níveis', Layers], ['mapa', 'Mapa de Expansão', Map],
  ['qualif', 'Qualificações', Award], ['cresc', 'Crescimento', TrendingUp],
  ['alertas', 'Alertas de Licença', Clock],
]
const dtBR = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—')
function licCor(d) { if (d == null) return 'text-gray-400'; if (d < 0) return 'text-red-600 font-bold'; if (d <= 30) return 'text-orange-600 font-semibold'; if (d <= 90) return 'text-amber-600'; return 'text-emerald-600' }
function licLabel(d) { if (d == null) return '—'; if (d < 0) return 'Vencida'; return `${d} dias` }
function exportarCSV(rows) {
  const head = ['Nome', 'Codigo', 'Cargo', 'Cidade', 'UF', 'PV', 'GV', 'Diretos', 'Equipe', 'Status', 'Licenca venc.', 'Dias']
  const linhas = rows.map((l) => [l.nome, l.codigo_indicacao || '', l.cargo || '', l.cidade || '', l.estado || '',
    l.pv || 0, l.gv || 0, l.total_diretos ?? l.total_indicados ?? 0, l.total_equipe ?? 0, l.ativo ? 'Ativo' : 'Inativo',
    l.licenca_vencimento || '', l.dias_licenca ?? ''].map((x) => `"${String(x).replace(/"/g, '""')}"`).join(';'))
  const csv = head.join(';') + '\n' + linhas.join('\n')
  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  a.download = `rede_${new Date().toISOString().slice(0, 10)}.csv`; a.click()
}

// ── borda de licença por dias restantes (indicador visual) ──
function bordaLicenca(node) {
  const d = node.dias_licenca
  if (!node.ativo) return '#a8a8a8'
  if (d == null) return '#16a34a'
  if (d < 0) return '#dc2626'
  if (d <= 30) return '#f59e0b'
  return '#16a34a'
}

// ── nó da árvore: CARD horizontal (org-chart) com expandir/recolher ──
function NoCard({ node, depth = 0 }) {
  const [aberto, setAberto] = useState(depth < 1)   // nível 1 aberto; demais sob demanda
  if (!node) return null
  const filhos = node.indicados || []
  const temFilhos = filhos.length > 0
  return (
    <div className="flex flex-col items-center">
      <div className="bg-white rounded-2xl px-3 py-2 shadow-sm border-2 min-w-[180px] text-center relative" style={{ borderColor: bordaLicenca(node) }}>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-bold mx-auto mb-1">{(node.nome || '?').charAt(0).toUpperCase()}</div>
        <p className="font-semibold text-xs text-gray-900 leading-tight">{node.nome}</p>
        {node.cargo && node.cargo !== '—' && <span className="inline-block text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold mt-0.5">{node.cargo}</span>}
        <div className="grid grid-cols-2 gap-x-2 text-[10px] text-gray-500 mt-1">
          <span>PV {num(node.pv)}</span><span>VQ {num(node.gv)}</span>
          <span>👥 {num(node.total_clientes)}</span><span>🤝 {num(node.total_diretos ?? node.total_indicados)}</span>
        </div>
        {temFilhos && (
          <button onClick={() => setAberto(!aberto)} className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center shadow">{aberto ? '−' : '+'}</button>
        )}
      </div>
      {temFilhos && aberto && (
        <>
          <div className="w-px h-4 bg-gray-300" />
          <div className="flex gap-4 items-start border-t-2 border-gray-200 pt-4 px-2" style={{ borderTopWidth: filhos.length > 1 ? 2 : 0 }}>
            {filhos.map((ch) => <NoCard key={ch.id} node={ch} depth={depth + 1} />)}
          </div>
        </>
      )}
    </div>
  )
}

function Card({ icon: Icon, label, value, color, t }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow mb-2`}><Icon size={16} className="text-white" /></div>
      <p className="text-xl font-heading font-bold text-gray-900">{value}</p>
      <p className={`text-xs font-medium ${t} mt-0.5`}>{label}</p>
    </div>
  )
}

export default function MinhaRede() {
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const meuLic = load('licenciado')
  const [tab, setTab] = useState('resumo')
  const [licSel, setLicSel] = useState(meuLic?.id || '')   // admin escolhe; licenciado = próprio
  const [opcoesLic, setOpcoesLic] = useState([])
  const [painel, setPainel] = useState(null)
  const [arvore, setArvore] = useState(null)
  const [lista, setLista] = useState([])
  const [clientes, setClientes] = useState(null)
  const [alertas, setAlertas] = useState(null)
  const [historico, setHistorico] = useState([])
  const [zoom, setZoom] = useState(0.9)
  const [busca, setBusca] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isAdmin) api.get('/licenciados?limit=100').then((r) => setOpcoesLic(r.data.data || [])).catch(() => {})
  }, [isAdmin])

  const carregar = useCallback(() => {
    setLoading(true)
    const qs = licSel ? `?licenciado_id=${licSel}` : ''
    Promise.all([
      api.get(`/rede/painel${qs}`).then((r) => setPainel(r.data)).catch(() => setPainel(null)),
      api.get('/licenciados?limit=200').then((r) => setLista(r.data.data || [])).catch(() => setLista([])),
      api.get('/clientes/rede').then((r) => setClientes(r.data)).catch(() => setClientes({ diretos: [], indiretos: [] })),
      api.get(`/rede/alertas-licenca${qs}`).then((r) => setAlertas(r.data)).catch(() => setAlertas(null)),
      api.get(`/carreira/historico${licSel ? '?licenciado_id=' + licSel : ''}`).then((r) => setHistorico(r.data.data || [])).catch(() => setHistorico([])),
    ]).finally(() => setLoading(false))
    const rootId = licSel || meuLic?.id
    if (rootId) api.get(`/licenciados/${rootId}/rede`).then((r) => setArvore(r.data)).catch(() => setArvore(null))
  }, [licSel]) // eslint-disable-line
  useEffect(() => { carregar() }, [carregar])

  const rootId = licSel || meuLic?.id
  const diretos = lista.filter((l) => String(l.sponsor_id) === String(rootId))
  const listaFiltrada = busca ? lista.filter((l) => (l.nome || '').toLowerCase().includes(busca.toLowerCase()) || String(l.id).includes(busca) || (l.cidade || '').toLowerCase().includes(busca.toLowerCase())) : lista
  const R = painel?.resumo || {}

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Minha Rede</h1>
          <p className="text-sm text-gray-500 mt-0.5">Acompanhe crescimento, qualificação, clientes e produção da equipe.</p>
        </div>
        {isAdmin && (
          <SearchableSelect value={licSel} onChange={setLicSel} placeholder="Rede de (licenciado)..." className="w-64"
            options={opcoesLic.map((l) => ({ value: l.id, label: l.nome }))} />
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === k ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader text="Carregando rede..." /></div> : (
        <>
          {tab === 'resumo' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card icon={Users} label="Clientes Ativos" value={num(R.clientes_ativos)} color="from-emerald-400 to-teal-400" t="text-emerald-700" />
                <Card icon={UserCheck} label="Licenciados Ativos" value={num(R.licenciados_ativos)} color="from-indigo-400 to-violet-400" t="text-indigo-700" />
                <Card icon={Users} label="Novos Clientes (mês)" value={num(R.novos_clientes_mes)} color="from-blue-400 to-cyan-400" t="text-blue-700" />
                <Card icon={UserCheck} label="Novos Licenciados (mês)" value={num(R.novos_licenciados_mes)} color="from-amber-400 to-orange-400" t="text-amber-700" />
                <Card icon={TrendingUp} label="PV Total (rede)" value={num(R.pv_total)} color="from-rose-400 to-pink-400" t="text-rose-700" />
                <Card icon={Zap} label="Comissões do Mês" value={money(R.comissoes_mes)} color="from-emerald-400 to-teal-400" t="text-accent-700" />
                <Card icon={Network} label="Conexões Ativas" value={num(R.conexoes_ativas)} color="from-violet-400 to-purple-400" t="text-violet-700" />
                <Card icon={Award} label="Status de Carreira" value={R.status_carreira || '—'} color="from-amber-400 to-yellow-500" t="text-amber-700" />
              </div>
            </div>
          )}

          {tab === 'arvore' && (
            <div className="bg-gray-50/40 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2 p-2 border-b border-gray-100">
                <span className="text-[11px] text-gray-400 mr-auto flex items-center gap-2 flex-wrap pl-1">
                  Licença: <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> ok</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> ≤30d</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-600" /> vencida</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> inativo</span>
                </span>
                <button onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} className="w-7 h-7 rounded-lg bg-white border border-gray-200 font-bold">−</button>
                <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))} className="w-7 h-7 rounded-lg bg-white border border-gray-200 font-bold">+</button>
                <button onClick={() => setZoom(0.9)} className="px-2 h-7 rounded-lg bg-white border border-gray-200 text-xs">Ajustar</button>
              </div>
              <div className="overflow-auto p-6" style={{ maxHeight: '70vh' }}>
                {arvore ? (
                  <div className="inline-block origin-top-left transition-transform" style={{ transform: `scale(${zoom})` }}>
                    <NoCard node={arvore} />
                  </div>
                ) : <p className="text-gray-400 text-sm p-4">{isAdmin ? 'Selecione um licenciado acima.' : 'Sem dados de rede.'}</p>}
              </div>
            </div>
          )}

          {(tab === 'lista') && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 max-w-sm">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, ID, cidade..."
                    className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:border-accent-400" />
                </div>
                <button onClick={() => exportarCSV(listaFiltrada)} className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-xl text-xs font-semibold hover:bg-gray-50"><Download size={14} /> Exportar CSV</button>
              </div>
              <RedeTabela rows={listaFiltrada} />
            </div>
          )}

          {tab === 'diretos' && <RedeTabela rows={diretos} vazio="Você ainda não tem patrocinados diretos." />}

          {tab === 'clientes' && <ClientesRedeView data={clientes} />}

          {tab === 'niveis' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Nível</th><th className="px-4 py-2 text-right">Licenciados</th><th className="px-4 py-2 text-right">Clientes</th><th className="px-4 py-2 text-right">PV</th></tr></thead>
                <tbody>
                  {(painel?.niveis || []).map((n) => (
                    <tr key={n.nivel} className="border-t border-gray-100"><td className="px-4 py-2 font-medium">Nível {n.nivel}</td><td className="px-4 py-2 text-right">{num(n.licenciados)}</td><td className="px-4 py-2 text-right">{num(n.clientes)}</td><td className="px-4 py-2 text-right">{num(n.pv)}</td></tr>
                  ))}
                  {(painel?.niveis || []).length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Sem níveis abaixo.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'mapa' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Estado</th><th className="px-4 py-2 text-right">Licenciados</th><th className="px-4 py-2 text-right">Clientes</th></tr></thead>
                <tbody>
                  {(painel?.mapa || []).map((m, i) => (
                    <tr key={i} className="border-t border-gray-100"><td className="px-4 py-2 font-medium">{m.estado}</td><td className="px-4 py-2 text-right">{num(m.licenciados)}</td><td className="px-4 py-2 text-right">{num(m.clientes)}</td></tr>
                  ))}
                  {(painel?.mapa || []).length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Sem dados.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'qualif' && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 max-w-xl">
              <p className="text-xs text-gray-500 uppercase">Status de Carreira</p>
              <p className="text-2xl font-heading font-bold text-gray-900 flex items-center gap-2"><Award size={20} className="text-amber-500" /> {R.status_carreira || '—'}</p>
              <p className="text-sm text-gray-600 mt-1">Qualificação do mês: <b>{R.qualificacao || '—'}</b></p>
              {R.proxima_faixa ? (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Para {R.proxima_faixa}</span><span>{R.progresso_proxima ?? 0}%</span></div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-accent-500" style={{ width: `${R.progresso_proxima ?? 0}%` }} /></div>
                  <p className="text-[11px] text-gray-400 mt-1">Meta: {num(R.proxima_meta_pv)} de GV (atual: {num(R.gv)})</p>
                </div>
              ) : <p className="text-xs text-gray-400 mt-2">Faixa máxima atingida 🎉</p>}
              <p className="text-[11px] text-gray-400 mt-3 border-t border-gray-100 pt-2">Status permanente só é cravado após manter a faixa por ≥1 fechamento mensal.</p>
            </div>
          )}

          {tab === 'qualif' && historico.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 px-4 pt-3">Histórico mensal de qualificação</p>
              <table className="w-full text-sm mt-1">
                <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Mês</th><th className="px-4 py-2 text-right">Volume Total</th><th className="px-4 py-2">Qualificação</th><th className="px-4 py-2">Status</th></tr></thead>
                <tbody>
                  {historico.map((h) => (
                    <tr key={h.mes} className="border-t border-gray-100"><td className="px-4 py-2">{h.mes}</td><td className="px-4 py-2 text-right">{num(h.volume_total)}</td><td className="px-4 py-2">{h.qualificacao || '—'}</td><td className="px-4 py-2 text-gray-500">{h.status_permanente || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'cresc' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card icon={Users} label="Novos Clientes (mês)" value={num(painel?.crescimento?.novos_clientes_mes)} color="from-blue-400 to-cyan-400" t="text-blue-700" />
              <Card icon={UserCheck} label="Novos Licenciados (mês)" value={num(painel?.crescimento?.novos_licenciados_mes)} color="from-amber-400 to-orange-400" t="text-amber-700" />
              <Card icon={Users} label="Novos Clientes (ano)" value={num(painel?.crescimento?.novos_clientes_ano)} color="from-emerald-400 to-teal-400" t="text-emerald-700" />
              <Card icon={UserCheck} label="Novos Licenciados (ano)" value={num(painel?.crescimento?.novos_licenciados_ano)} color="from-indigo-400 to-violet-400" t="text-indigo-700" />
              <Card icon={Network} label="Conexões Ativas" value={num(painel?.crescimento?.conexoes_ativas)} color="from-violet-400 to-purple-400" t="text-violet-700" />
            </div>
          )}

          {tab === 'alertas' && (
            <div className="space-y-3">
              {alertas?.resumo && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[['vencida', 'Vencidas', 'text-red-600'], ['7d', '≤ 7 dias', 'text-red-500'], ['15d', '≤ 15 dias', 'text-orange-600'], ['30d', '≤ 30 dias', 'text-orange-500'], ['60d', '≤ 60 dias', 'text-amber-600'], ['90d', '≤ 90 dias', 'text-amber-500']].map(([k, l, c]) => (
                    <div key={k} className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center"><p className={`text-xl font-heading font-bold ${c}`}>{alertas.resumo[k] || 0}</p><p className="text-[11px] text-gray-500">{l}</p></div>
                  ))}
                </div>
              )}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-3 py-2">Licenciado</th><th className="px-3 py-2">Cidade/UF</th><th className="px-3 py-2">Carreira</th><th className="px-3 py-2">Vencimento</th><th className="px-3 py-2">Situação</th></tr></thead>
                  <tbody>
                    {(alertas?.alertas || []).map((a) => (
                      <tr key={a.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">{a.nome}</td>
                        <td className="px-3 py-2 text-gray-500">{[a.cidade, a.estado].filter(Boolean).join('/') || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{a.cargo || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{dtBR(a.vencimento)}</td>
                        <td className={`px-3 py-2 ${licCor(a.dias)}`}>{licLabel(a.dias)}</td>
                      </tr>
                    ))}
                    {(alertas?.alertas || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Nenhuma licença vencendo nos próximos 90 dias. 👍</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400">Alertas automáticos por WhatsApp/e-mail nos marcos 90/60/30/15/7 dias e no vencimento (disparo agendado).</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RedeTabela({ rows, vazio = 'Nenhum licenciado.' }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
      <table className="w-full text-sm whitespace-nowrap">
        <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
          <tr><th className="px-3 py-2">Nome</th><th className="px-3 py-2">Cód</th><th className="px-3 py-2">Plano de Carreira</th><th className="px-3 py-2">Cidade/UF</th><th className="px-3 py-2 text-right">PV</th><th className="px-3 py-2 text-right">GV</th><th className="px-3 py-2 text-right">Diretos</th><th className="px-3 py-2">Licença</th><th className="px-3 py-2">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-t border-gray-100 hover:bg-primary-50/30">
              <td className="px-3 py-2 font-medium text-gray-900">{l.nome}</td>
              <td className="px-3 py-2 text-gray-500">{l.codigo_indicacao || '—'}</td>
              <td className="px-3 py-2">{l.cargo || '—'}</td>
              <td className="px-3 py-2 text-gray-500">{[l.cidade, l.estado].filter(Boolean).join('/') || '—'}</td>
              <td className="px-3 py-2 text-right">{num(l.pv)}</td>
              <td className="px-3 py-2 text-right">{num(l.gv)}</td>
              <td className="px-3 py-2 text-right">{num(l.total_diretos ?? l.total_indicados)}</td>
              <td className={`px-3 py-2 text-xs ${licCor(l.dias_licenca)}`}>{licLabel(l.dias_licenca)}</td>
              <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${l.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{l.ativo ? 'Ativo' : 'Inativo'}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">{vazio}</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function ClientesRedeView({ data }) {
  const [sub, setSub] = useState('diretos')
  const diretos = data?.diretos || []; const indiretos = data?.indiretos || []
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button onClick={() => setSub('diretos')} className={`px-3 py-1 rounded-lg text-xs font-semibold ${sub === 'diretos' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200'}`}>Diretos ({diretos.length})</button>
        <button onClick={() => setSub('indiretos')} className={`px-3 py-1 rounded-lg text-xs font-semibold ${sub === 'indiretos' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200'}`}>Indiretos ({indiretos.length})</button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
            <tr><th className="px-3 py-2">Nome</th><th className="px-3 py-2">Serviço</th><th className="px-3 py-2">Cidade/UF</th>{sub === 'indiretos' && <th className="px-3 py-2">Licenciado</th>}<th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody>
            {(sub === 'diretos' ? diretos : indiretos).map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-800">{c.nome}</td>
                <td className="px-3 py-2">{c.produto || '—'}</td>
                <td className="px-3 py-2 text-gray-500">{[c.cidade, c.estado].filter(Boolean).join('/') || '—'}</td>
                {sub === 'indiretos' && <td className="px-3 py-2 text-gray-500">{c.licenciado || '—'}</td>}
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${c.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{c.ativo ? 'Ativo' : 'Inativo'}</span></td>
              </tr>
            ))}
            {(sub === 'diretos' ? diretos : indiretos).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Nenhum cliente.</td></tr>}
          </tbody>
        </table>
      </div>
      {sub === 'indiretos' && <p className="text-[11px] text-gray-400">Clientes indiretos: dados pessoais ocultos por privacidade (LGPD).</p>}
    </div>
  )
}
